import React from 'react';
import PropTypes from 'prop-types';

import { routerActions } from 'connected-react-router';
import { Redirect } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { isBefore } from 'date-fns';

import { notAllowed, notAllowedOnus, loginPath } from '@navigation/routes';
import { deleteLoginValues } from '@store/login/actions';
import { isAllowed } from '@helpers/auth';
import { useAuth } from '@hooks';

/**
 * Wrapper component that works as Authentication Middleware
 *
 * @component
 * @param {Object} params - Component params
 * @param {Object} params.children - Children component
 * @param {Object} params.route - Route configuration
 * @example
 * const route = {
 *    name: "LOGIN"
 *    path: "/login",
 *    private: true,
 *    showOnNav: false,
 *    component: <Component/>,
 *  };
 * return (
 * <AuthProvider route={route}>
 *  <Page />
 * </AuthProvider>
 * )
 */
const AuthProvider = ({ children, route }) => {
  const { auth: login, isOnus } = useAuth();
  const dispatch = useDispatch();

  let redirectTo;

  if (isOnus) {
    redirectTo = notAllowedOnus;
  } else {
    redirectTo = notAllowed;
  }

  if (route.private) {
    if (login.jwt && login.loguedAs) {
      if (isBefore(new Date((login?.auth?.exp || 0) * 1000), new Date())) {
        dispatch(deleteLoginValues(true));
        dispatch(routerActions.push('/login'));
        return <Redirect to={loginPath} />;
      }
      const { roles } = login.loguedAs;
      if (login.loguedAs.is_legal_agent && roles.indexOf('LEGAL_REPRESENTATIVE') === -1) {
        roles.push('LEGAL_REPRESENTATIVE');
      }
      if (isAllowed(route.name.replace('_SUCCESS', '').replace('_ERROR', ''))) {
        return <>{children}</>;
      }
      return <Redirect to={redirectTo} />;
    }
    return <Redirect to={loginPath} />;
  }
  return <>{children}</>;
};

AuthProvider.propTypes = {
  children: PropTypes.element.isRequired,
  route: PropTypes.shape({
    private: PropTypes.bool,
    name: PropTypes.string.isRequired,
    path: PropTypes.string.isRequired,
    showOnNav: PropTypes.bool.isRequired,
    component: PropTypes.any.isRequired,
  }).isRequired,
};

export default AuthProvider;
